import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { AuthFrame } from '@/components/AuthFrame';
import { meQuery } from '@/lib/session';

export const Route = createFileRoute('/oauth/consent')({
  // The signed authorization parameters travel back to the server unchanged (oauth_query).
  validateSearch: z.looseObject({ client_id: z.string().optional(), redirect_uri: z.string().optional(), scope: z.string().optional() }),
  component: ConsentPage
});

type PublicClient = { client_name?: string };

/**
 * Claude Code asks to act for the signed-in user, either as their Assistant or as a
 * coding session (PLAN.md §5.3). What each may do is fixed server-side by scope and
 * endpoint; the text below says exactly that.
 */
function ConsentPage() {
  const { t } = useTranslation();
  const { client_id: clientId, redirect_uri: redirectUri, scope = '' } = Route.useSearch();
  const me = useQuery({ ...meQuery(), retry: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const client = useQuery({
    queryKey: ['oauth-client', clientId],
    enabled: !!clientId && me.isSuccess,
    queryFn: async () => {
      const res = await fetch(`/api/auth/oauth2/public-client?client_id=${encodeURIComponent(clientId!)}`, { credentials: 'same-origin' });
      if (!res.ok) throw new Error(String(res.status));
      return (await res.json()) as PublicClient;
    }
  });

  // Not signed in (session expired between steps): sign in, then come back here.
  useEffect(() => {
    if (me.isError) window.location.assign(`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`);
  }, [me.isError]);

  const decide = async (accept: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/auth/oauth2/consent', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ accept, oauth_query: window.location.search.slice(1) })
      });
      const body = (await res.json().catch(() => ({}))) as { url?: string };
      if (!res.ok || !body.url) throw new Error(String(res.status));
      window.location.assign(body.url);
    } catch {
      setError(t('oauth.failed'));
      setBusy(false);
    }
  };

  const host = (u: string | undefined) => {
    try {
      return u ? new URL(u).host : null;
    } catch {
      return null;
    }
  };
  const scopes = scope.split(/\s+/);
  const name = client.data?.client_name ?? t('oauth.unknownClient');
  const metadataHost = clientId?.startsWith('https://') ? host(clientId) : null;
  const redirectHost = host(redirectUri);
  return (
    <AuthFrame title={t('oauth.title', { client: name })} lead={me.data ? t('oauth.signedInAs', { name: me.data.name, email: me.data.email }) : undefined}>
      {!clientId ? (
        <p className="text-error text-sm">{t('oauth.invalid')}</p>
      ) : (
        <div className="space-y-4 text-sm">
          <div className={`alert alert-soft text-sm ${metadataHost ? 'alert-info' : 'alert-warning'}`}>
            <div className="space-y-1">
              <p>{metadataHost ? t('oauth.verifiedBy', { host: metadataHost }) : t('oauth.unverified')}</p>
              {redirectHost && (
                <p>
                  {t('oauth.redirectsTo')} <strong className="font-mono">{redirectHost}</strong>
                </p>
              )}
            </div>
          </div>
          {scopes.includes('assistant') && (
            <div>
              <p className="font-medium">{t('oauth.assistant')}</p>
              <p>{t('oauth.assistantCan')}</p>
            </div>
          )}
          {scopes.includes('worker') && (
            <div>
              <p className="font-medium">{t('oauth.worker')}</p>
              <p>{t('oauth.workerCan')}</p>
            </div>
          )}
          <div>
            <p className="font-medium">{t('oauth.never')}</p>
            <p>{t('oauth.cannot')}</p>
          </div>
          <p className="text-base-content/60 text-xs">{t('oauth.revoke')}</p>
          {error && <p className="text-error">{error}</p>}
          <div className="flex gap-2">
            <button type="button" className="btn btn-ghost flex-1" disabled={busy} onClick={() => void decide(false)}>
              {t('oauth.deny')}
            </button>
            <button type="button" className="btn btn-primary flex-1" disabled={busy || !me.data} onClick={() => void decide(true)}>
              {t('oauth.allow')}
            </button>
          </div>
        </div>
      )}
    </AuthFrame>
  );
}
